import {
  CreateOrderTypeRequest,
  OrderType,
  OrderTypeCustomFieldDataType,
  UpdateOrderTypeRequest
} from '../../../../@types/orderTypes';
import { isValidCustomFieldValue } from '../../../../utils/validation/isValidCustomFieldValue';

export const ORDER_TYPE_KEY_FORMAT = /^[a-z0-9]+(-[a-z0-9]+)*$/;

const KEY_MAX_LENGTH = 64;
const NAME_MAX_LENGTH = 128;
const UNIT_NAME_MAX_LENGTH = 64;
const FIELD_KEY_MAX_LENGTH = 64;
const FIELD_LABEL_MAX_LENGTH = 128;
const FIELD_DEFAULT_VALUE_MAX_LENGTH = 512;
const FIELD_ENUM_VALUES_MAX_LENGTH = 2048;

export type OrderTypeFieldDraft = {
  draftId: string;
  key: string;
  label: string;
  dataType: OrderTypeCustomFieldDataType;
  isRequired: boolean;
  defaultValue: string;
  enumValues: string[];
};

export type OrderTypeDraft = {
  id: string | null;
  key: string;
  name: string;
  unitName: string;
  isMetered: boolean;
  isActive: boolean;
  fields: OrderTypeFieldDraft[];
  claims: string[];
};

export type OrderTypeDraftError = {
  code:
    | 'keyRequired'
    | 'keyInvalid'
    | 'keyTooLong'
    | 'keyInUse'
    | 'nameRequired'
    | 'nameTooLong'
    | 'unitNameRequired'
    | 'unitNameTooLong'
    | 'fieldKeyRequired'
    | 'fieldKeyTooLong'
    | 'fieldKeysMustBeUnique'
    | 'fieldLabelTooLong'
    | 'fieldDefaultValueTooLong'
    | 'fieldEnumValuesRequired'
    | 'fieldEnumValuesMustBeUnique'
    | 'fieldEnumValuesTooLong'
    | 'fieldDefaultValueInvalid';
  fieldKey?: string;
};

export const emptyOrderTypeDraft = (): OrderTypeDraft => ({
  id: null,
  key: '',
  name: '',
  unitName: '',
  isMetered: true,
  isActive: true,
  fields: [],
  claims: []
});

export const toOrderTypeDraft = (orderType: OrderType): OrderTypeDraft => ({
  id: orderType.id,
  key: orderType.key,
  name: orderType.name,
  unitName: orderType.unitName,
  isMetered: orderType.isMetered,
  isActive: orderType.isActive,
  fields: orderType.customFields.map((field) => ({
    draftId: field.id,
    key: field.key,
    label: field.label ?? '',
    dataType: field.dataType,
    isRequired: field.isRequired,
    defaultValue: field.defaultValue ?? '',
    enumValues: field.enumValues
  })),
  claims: orderType.claims
});

const validateField = (field: OrderTypeFieldDraft): OrderTypeDraftError | null => {
  const key = field.key.trim();
  if (!key) {
    return { code: 'fieldKeyRequired' };
  }
  if (key.length > FIELD_KEY_MAX_LENGTH) {
    return { code: 'fieldKeyTooLong', fieldKey: key };
  }
  if (field.label.trim().length > FIELD_LABEL_MAX_LENGTH) {
    return { code: 'fieldLabelTooLong', fieldKey: key };
  }
  const defaultValue = field.defaultValue.trim();
  if (defaultValue.length > FIELD_DEFAULT_VALUE_MAX_LENGTH) {
    return { code: 'fieldDefaultValueTooLong', fieldKey: key };
  }

  if (field.dataType === OrderTypeCustomFieldDataType.Enum) {
    const enumValues = field.enumValues.filter((value) => value.trim());
    if (enumValues.length === 0) {
      return { code: 'fieldEnumValuesRequired', fieldKey: key };
    }
    if (new Set(enumValues).size !== enumValues.length) {
      return { code: 'fieldEnumValuesMustBeUnique', fieldKey: key };
    }
    if (JSON.stringify(enumValues).length > FIELD_ENUM_VALUES_MAX_LENGTH) {
      return { code: 'fieldEnumValuesTooLong', fieldKey: key };
    }
  }

  if (defaultValue && !isValidCustomFieldValue(field.dataType, field.enumValues, defaultValue)) {
    return { code: 'fieldDefaultValueInvalid', fieldKey: key };
  }

  return null;
};

const validateBaseFields = (draft: OrderTypeDraft): OrderTypeDraftError | null => {
  if (!draft.name.trim()) {
    return { code: 'nameRequired' };
  }
  if (draft.name.trim().length > NAME_MAX_LENGTH) {
    return { code: 'nameTooLong' };
  }
  if (!draft.unitName.trim()) {
    return { code: 'unitNameRequired' };
  }
  if (draft.unitName.trim().length > UNIT_NAME_MAX_LENGTH) {
    return { code: 'unitNameTooLong' };
  }
  return null;
};

export const validateCreateDraft = (
  draft: OrderTypeDraft,
  existingKeys: string[]
): OrderTypeDraftError | null => {
  const key = draft.key.trim();
  if (!key) {
    return { code: 'keyRequired' };
  }
  if (!ORDER_TYPE_KEY_FORMAT.test(key)) {
    return { code: 'keyInvalid' };
  }
  if (key.length > KEY_MAX_LENGTH) {
    return { code: 'keyTooLong' };
  }
  if (existingKeys.includes(key)) {
    return { code: 'keyInUse' };
  }

  const baseError = validateBaseFields(draft);
  if (baseError) {
    return baseError;
  }

  const fieldKeys = draft.fields.map((field) => field.key.trim().toLowerCase());
  if (new Set(fieldKeys).size !== fieldKeys.length) {
    return { code: 'fieldKeysMustBeUnique' };
  }

  for (const field of draft.fields) {
    const fieldError = validateField(field);
    if (fieldError) {
      return fieldError;
    }
  }

  return null;
};

export const validateEditDraft = (draft: OrderTypeDraft): OrderTypeDraftError | null =>
  validateBaseFields(draft);

export const toCreateOrderTypeRequest = (draft: OrderTypeDraft): CreateOrderTypeRequest => ({
  key: draft.key.trim(),
  name: draft.name.trim(),
  unitName: draft.unitName.trim(),
  isMetered: draft.isMetered,
  isActive: draft.isActive,
  customFields: draft.fields.map((field, index) => ({
    key: field.key.trim(),
    label: field.label.trim() || null,
    dataType: field.dataType,
    isRequired: field.isRequired,
    defaultValue: field.defaultValue.trim() || null,
    enumValues:
      field.dataType === OrderTypeCustomFieldDataType.Enum
        ? field.enumValues.filter((value) => value.trim())
        : null,
    sortOrder: index
  })),
  claims: draft.claims
});

export const toUpdateOrderTypeRequest = (draft: OrderTypeDraft): UpdateOrderTypeRequest => ({
  name: draft.name.trim(),
  unitName: draft.unitName.trim(),
  isActive: draft.isActive,
  claims: draft.claims
});
